import { useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import {
    FormCheckbox,
    RadioInput,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import {
    IEditNoShowNoteMedPayload,
    IGetClientNotes,
    ISessionNote,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import { NO_SHOW_NOTE_MED_LABELS } from "../../../../utils/constants";
import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import MedNoteEPrescribe from "../../../../modules/e-prescirbe/components/MedNoteEPrescribe/MedNoteEPrescribe";

interface EditNoShowNoteMedProps {
    noteDetails: ISessionNote;
}

export default function EditNoShowNoteMed({
    noteDetails,
}: EditNoShowNoteMedProps) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);
    const isSigning = useRef(false);
    const { data: notes } = useFetchClientNotesList(
        noteDetails.client.client_id,
        {
            type: noteDetails.type,
            load_previous_notes: true,
            provider_id: noteDetails.provider.provider_id,
        }
    );
    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();
    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        watch,
        control,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditNoShowNoteMedPayload>({
        resolver: yupResolver(
            yup.object({
                location_of_the_client: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Client location is required")
                            : field
                    ),
                other_location_of_the_client: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_client"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other client location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Provider location is required")
                            : field
                    ),
                other_location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_provider"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other provider location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                consent_was_received_for_the_teletherapy_session: yup.bool(),
                the_session_was_conducted_via_video: yup.bool(),
                session_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("Session location is required"),
                    otherwise: yup.string(),
                }),
                reason_provided_by_client_and_when_notice_was_given: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("This field is required"),
                        otherwise: yup.string(),
                    }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: {
            location_of_the_client:
                noteDetails.note_content.location_of_the_client,
            other_location_of_the_client:
                noteDetails.note_content.other_location_of_the_client,
            location_of_the_provider:
                noteDetails.note_content.location_of_the_provider,
            other_location_of_the_provider:
                noteDetails.note_content.other_location_of_the_provider,
            consent_was_received_for_the_teletherapy_session:
                noteDetails.note_content
                    .consent_was_received_for_the_teletherapy_session,
            the_session_was_conducted_via_video:
                noteDetails.note_content.the_session_was_conducted_via_video,
            session_location: noteDetails.note_content.session_location ?? "",
            reason_provided_by_client_and_when_notice_was_given:
                noteDetails.note_content
                    .reason_provided_by_client_and_when_notice_was_given ?? "",
            signature: noteDetails.note_content.signature ?? "",
        },
        shouldFocusError: false,
    });
    const watchedValues = watch();

    const sessionLocationOptionsForSelect = Object.keys(SessionLocations).map(
        (location) => ({
            label: removeEnumUnderscore(location),
            value: SessionLocations[location as keyof typeof SessionLocations],
        })
    );

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() => {
                    setValue(
                        "location_of_the_client",
                        selectedNote.note_content.location_of_the_client
                    );
                    setValue(
                        "other_location_of_the_client",
                        selectedNote.note_content.other_location_of_the_client
                    );
                    setValue(
                        "location_of_the_provider",
                        selectedNote.note_content.location_of_the_provider
                    );
                    setValue(
                        "other_location_of_the_provider",
                        selectedNote.note_content.other_location_of_the_provider
                    );
                    setValue(
                        "consent_was_received_for_the_teletherapy_session",
                        selectedNote.note_content
                            .consent_was_received_for_the_teletherapy_session
                    );
                    setValue(
                        "the_session_was_conducted_via_video",
                        selectedNote.note_content
                            .the_session_was_conducted_via_video
                    );
                    setValue(
                        "session_location",
                        selectedNote.note_content.session_location ?? ""
                    );
                    setValue(
                        "reason_provided_by_client_and_when_notice_was_given",
                        selectedNote.note_content
                            .reason_provided_by_client_and_when_notice_was_given ??
                            ""
                    );
                    setShowPrompt(false);
                }}
            />
            {notes?.data.length ? (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes.data.map((note) => (
                                <DropdownItem
                                    key={note.note_id}
                                    onClick={() => {
                                        setSelectedNote(note);
                                        setShowPrompt(true);
                                    }}
                                >
                                    {makeStringFirstLetterCapital(
                                        removeEnumUnderscore(note.note_type)
                                    )}{" "}
                                    - Note content Details -{" "}
                                    {formatDate(note.date_of_service)}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            ) : null}
            <form
                id="edit-note-form"
                aria-label="no show medical note form"
                onSubmit={handleSubmit(handleSignNote)}
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <Controller
                        name="session_location"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={NO_SHOW_NOTE_MED_LABELS.session_location}
                                placeholder=""
                                options={sessionLocationOptionsForSelect}
                                onChange={(val) =>
                                    field.onChange(
                                        (val as { value: string }).value
                                    )
                                }
                                value={
                                    sessionLocationOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                hasError={!!errors.session_location}
                                errorText={errors.session_location?.message}
                            />
                        )}
                    />
                </div>
                {watch("session_location") === SessionLocations.Telehealth && (
                    <>
                        <h3 className="note-label-req">
                            {NO_SHOW_NOTE_MED_LABELS.location_of_the_client}
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_client")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_client === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_client ? (
                            <div className="note-error">
                                <p>{errors.location_of_the_client?.message}</p>
                            </div>
                        ) : null}
                        {watch("location_of_the_client") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_client"
                                    )}
                                    label={
                                        NO_SHOW_NOTE_MED_LABELS.other_location_of_the_client
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_client
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_client
                                    }
                                    errorText={
                                        errors.other_location_of_the_client
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <h3 className="note-label-req">
                            {NO_SHOW_NOTE_MED_LABELS.location_of_the_provider}
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_provider")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_provider === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_provider ? (
                            <div className="note-error">
                                <p>
                                    {errors.location_of_the_provider?.message}
                                </p>
                            </div>
                        ) : null}
                        {watch("location_of_the_provider") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_provider"
                                    )}
                                    label={
                                        NO_SHOW_NOTE_MED_LABELS.other_location_of_the_provider
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_provider
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_provider
                                    }
                                    errorText={
                                        errors.other_location_of_the_provider
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <div className="note-checkboxes">
                            <FormCheckbox
                                {...register(
                                    "consent_was_received_for_the_teletherapy_session"
                                )}
                                label={
                                    NO_SHOW_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.consent_was_received_for_the_teletherapy_session
                                }
                            />
                            <FormCheckbox
                                {...register(
                                    "the_session_was_conducted_via_video"
                                )}
                                label={
                                    NO_SHOW_NOTE_MED_LABELS.the_session_was_conducted_via_video
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.the_session_was_conducted_via_video
                                }
                            />
                        </div>
                    </>
                )}

                <div className="fg">
                    <TextArea
                        {...register(
                            "reason_provided_by_client_and_when_notice_was_given"
                        )}
                        label={
                            NO_SHOW_NOTE_MED_LABELS.reason_provided_by_client_and_when_notice_was_given
                        }
                        hasError={Boolean(
                            errors.reason_provided_by_client_and_when_notice_was_given
                        )}
                        errorText={
                            errors
                                .reason_provided_by_client_and_when_notice_was_given
                                ?.message
                        }
                        isRequired
                    />
                </div>

                <MedNoteEPrescribe noteDetails={noteDetails} />
                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            label="Provider's Initials"
                            autoComplete="off"
                            hasError={Boolean(errors.signature)}
                            errorText={errors.signature?.message}
                            isRequired
                        />
                    </div>
                </div>
                <EditNoteFooter
                    onSaveButtonClick={() => {
                        isSigning.current = false;
                        handleSubmit(handleEditDraftNote)();
                    }}
                    noteDOS={noteDetails.date_of_service}
                    onSubmitButtonClick={() => {
                        isSigning.current = true;
                    }}
                />
            </form>
        </>
    );
}
